
import { PlaceDetails } from "@/types/google-places";
import { supabase } from "@/integrations/supabase/client";

export const useGooglePlacesService = () => {
  const getPlaceDetails = async (placeId: string): Promise<PlaceDetails> => {
    // Only call Google Places API if it's a real place ID (not a database UUID)
    // UUIDs are 36 characters with a specific pattern: 8-4-4-4-12
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(placeId)) {
      console.log('Using saved address, skipping Google Places API call');
      throw new Error('Not a Google Places ID');
    }
    
    const map = new google.maps.Map(document.createElement('div'));
    const service = new google.maps.places.PlacesService(map);

    return new Promise((resolve, reject) => {
      service.getDetails(
        {
          placeId,
          fields: ['address_components', 'formatted_address']
        },
        (place: google.maps.places.PlaceResult | null, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            console.log('Raw Places API Response:', place);
            resolve(place as PlaceDetails);
          } else {
            reject(new Error(`Failed to fetch place details: ${status}`));
          }
        }
      );
    });
  };

  const extractAddressComponents = (addressComponents: google.maps.GeocoderAddressComponent[]) => {
    const getComponent = (type: string) => 
      addressComponents.find(component => component.types.includes(type))?.long_name || "";

    return {
      streetNumber: getComponent("street_number"),
      street: getComponent("route"),
      sublocality: getComponent("sublocality") || getComponent("sublocality_level_1"),
      city: getComponent("locality") || getComponent("sublocality"),
      province: getComponent("administrative_area_level_1"),
      postalCode: getComponent("postal_code"),
      country: getComponent("country"),
    };
  };

  const fetchSavedAddress = async (addressId: string) => {
    const { data, error } = await supabase
      .from('address_book')
      .select('*')
      .eq('id', addressId)
      .single();

    if (error) throw error;
    return data;
  };

  return {
    getPlaceDetails,
    extractAddressComponents,
    fetchSavedAddress,
  };
};
